import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Star,
  MapPin,
  Bot,
  FileText,
  Inbox,
  Wrench,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  DollarSign,
  Briefcase,
  TrendingUp,
  Users,
  CheckCircle2,
  Circle,
  PhoneCall,
  Clock,
  Package,
  AlertTriangle,
  Loader2,
  CircleDollarSign,
  AlertCircle,
  Menu,
  Bell,
  Calendar as CalendarIcon,
  Plus,
  ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";
import { techniciansApi, jobsApi, estimatesApi, tasksApi, dashboardApi, pricebookApi } from "../../lib/api";
import { isLowStock } from "../Pricebook";
import Tasks from "../Tasks";
import MobileJobs from "./MobileJobs";
import HistoryTab, { getWeekBounds } from "../../components/mobile/HistoryTab";
import { MobileJobCard } from "../../components/mobile/MobileJobCard";

/* ---------------- design tokens (see /app/design_guidelines.json) ---------------- */
const BRAND_BLUE = "#0077CC";
const BRAND_SKY = "#66B2E6";

/* ---------------- shared helpers ---------------- */
const money0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => ymd(new Date());

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}
function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
function fmtHour(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function fmtDateLong(d) {
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}
function fmtDueShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

const STATUS_COLORS = {
  Scheduled: "#2563EB",
  "En Route": "#F97316",
  "In Progress": "#F59E0B",
  Completed: "#10B981",
  Cancelled: "#94A3B8",
};

const activityIconMap = {
  check: CheckCircle2, finished: CheckCircle2, dispatched: PhoneCall, arrived: MapPin,
  started: TrendingUp, invoiced: CircleDollarSign, note: AlertCircle, photo: Star,
  created: Briefcase, scheduled: Clock, enroute: MapPin, status: AlertCircle,
};
const activityColorMap = {
  finished: "#10B981", dispatched: "#F59E0B", arrived: "#10B981", started: "#2563EB",
  invoiced: "#2563EB", note: "#0EA5E9", photo: "#EC4899", created: "#64748B",
  scheduled: "#2563EB", enroute: "#F97316", status: "#475569",
};

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "trailing30", label: "Trailing 30 Days" },
];

function periodWindow(period) {
  const now = new Date();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (period === "week") {
    const dow = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dow);
  } else if (period === "month") {
    start.setDate(1);
  } else if (period === "trailing30") {
    start.setDate(start.getDate() - 29);
  }
  return { start, end: now };
}
const inWindow = (iso, start, end) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d >= start && d <= end;
};

function computeMetrics(myJobs, myEstimates, perf = {}, period) {
  const { start, end } = periodWindow(period);
  const jobsInWin = myJobs.filter((j) => inWindow(j.start, start, end));
  const revenue = jobsInWin.reduce((s, j) => s + Number(j.value || 0), 0);
  const jobs = jobsInWin.length;
  const estInWin = myEstimates.filter((e) => inWindow(e.issuedAt, start, end));
  const accepted = estInWin.filter((e) => ["Accepted", "Converted"].includes(e.status)).length;
  return {
    jobs,
    revenue,
    avgTicket: jobs > 0 ? revenue / jobs : 0,
    conversionRate: estInWin.length > 0 ? (accepted / estInWin.length) * 100 : 0,
    rating: Number(perf.rating || 0),
    reviews: Number(perf.reviewCount || 0),
  };
}

const TABS = [
  { key: "overview", label: "Overview", testid: "mobile-tab-overview" },
  { key: "all-jobs", label: "All Jobs", testid: "mobile-tab-all-jobs" },
  { key: "tasks", label: "Tasks", testid: "mobile-tab-tasks" },
  { key: "follow-up", label: "Follow Up", testid: "mobile-tab-follow-up" },
  { key: "history", label: "History", testid: "mobile-tab-history" },
  { key: "guru", label: "Guru", testid: "mobile-tab-guru" },
];

/**
 * MobileHome — the single mobile home surface for EVERY role.
 *
 * Same shell (top bar + drawer + bottom nav, mounted globally) and the same
 * six tabs (Overview / All Jobs / Tasks / Follow Up / History / Guru) for
 * everyone. Content inside each tab is scoped by role:
 *   • Technicians (or an admin/dispatcher previewing one from `/mobile`)
 *     see their own scorecard, jobs, and follow-ups.
 *   • Everyone else sees company-wide KPIs, all jobs, and (admin/dispatcher)
 *     low-stock alerts.
 */
export default function MobileHome() {
  const { user, hasModule } = useAuth();
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const userIsTech = user?.role === "technician";
  // Reached via the Sidebar's "Mobile Tech" preview link (admin/dispatcher
  // previewing a technician's dashboard) — same surface a real tech lands on.
  const isMobileRoute = location.pathname === "/mobile";
  const showTechView = userIsTech || isMobileRoute;
  const isPreview = showTechView && !userIsTech;
  const canSeeLowStock = !showTechView && (user?.role === "admin" || user?.role === "dispatcher");

  const [techs, setTechs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pricebook, setPricebook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");
  const [period, setPeriod] = useState("trailing30");
  const [mode, setMode] = useState("total");
  const [previewTechId, setPreviewTechId] = useState("");

  // Keep activeTab in sync with the URL so the global MobileBottomNav
  // (which navigates to /mobile?tab=... or /dashboard?tab=...) can switch
  // tabs from any route.
  const prevUrlTabRef = useRef(searchParams.get("tab"));
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === prevUrlTabRef.current) return;
    prevUrlTabRef.current = t;
    if (t) setActiveTab(t);
  }, [searchParams]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeTab && activeTab !== "overview") next.set("tab", activeTab); else next.delete("tab");
    if (next.toString() !== searchParams.toString()) {
      prevUrlTabRef.current = next.get("tab");
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  /* ---------------- data loading (branches by role) ---------------- */
  const loadAll = async () => {
    if (showTechView) {
      const [t, j, e] = await Promise.all([
        techniciansApi.list(),
        jobsApi.list(),
        estimatesApi.list().catch(() => []),
      ]);
      setTechs(t || []);
      setJobs(j || []);
      setEstimates(e || []);
    } else {
      const calls = [dashboardApi.stats(), dashboardApi.activity(8), jobsApi.list(), estimatesApi.list().catch(() => [])];
      if (canSeeLowStock) calls.push(pricebookApi.list());
      const results = await Promise.all(calls);
      const [s, a, j, e] = results;
      setStats(s);
      setActivity(a || []);
      setJobs(j || []);
      setEstimates(e || []);
      if (canSeeLowStock && results.length > 4) setPricebook(results[4] || []);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadAll()
      .catch(() => toast.error("Failed to load your dashboard"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTechView]);

  useEffect(() => {
    const refresh = () => loadAll().catch(() => {});
    const id = setInterval(refresh, 20000);
    const onVisible = () => { if (!document.hidden) refresh(); };
    window.addEventListener("focus", refresh);
    window.addEventListener("job-status-changed", refresh);
    window.addEventListener("job-created", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("job-status-changed", refresh);
      window.removeEventListener("job-created", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTechView]);

  // Lightweight fetch just for the Tasks tab badge — the tab body itself
  // embeds the full <Tasks/> page, which manages its own data.
  const loadTaskCount = () => tasksApi.list(showTechView ? { mine: true } : {}).then(setTasks).catch(() => {});
  useEffect(() => { loadTaskCount(); }, [showTechView]);
  useEffect(() => {
    window.addEventListener("tasks-changed", loadTaskCount);
    return () => window.removeEventListener("tasks-changed", loadTaskCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTechView]);
  const openTaskCount = useMemo(
    () => tasks.filter((t) => t.status !== "done" && (!user?.id || (t.assigneeIds || []).includes(user.id))).length,
    [tasks, user],
  );

  /* ---------------- tech-perspective derived data ---------------- */
  const tech = useMemo(() => {
    if (!showTechView || !techs.length) return null;
    if (isPreview) return techs.find((t) => t.id === previewTechId) || techs[0];
    const email = (user?.email || "").toLowerCase();
    return techs.find((t) => (t.email || "").toLowerCase() === email) || null;
  }, [showTechView, techs, isPreview, previewTechId, user]);

  const myJobs = useMemo(() => (tech ? jobs.filter((j) => j.techId === tech.id) : []), [jobs, tech]);
  const myJobIds = useMemo(() => new Set(myJobs.map((j) => j.id)), [myJobs]);

  const todaysJobs = useMemo(() => {
    const ts = todayStr();
    const pool = showTechView ? myJobs : jobs;
    return pool
      .filter((j) => (j.start || "").slice(0, 10) === ts && j.status !== "Completed")
      .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  }, [showTechView, myJobs, jobs]);

  const activeJobs = useMemo(
    () => myJobs.filter((j) => !["Completed", "Canceled"].includes(j.status)).sort((a, b) => (a.start || "").localeCompare(b.start || "")),
    [myJobs],
  );
  const currentWeek = useMemo(() => getWeekBounds(new Date()), []);
  const visibleActiveJobs = useMemo(() => {
    if (!userIsTech) return activeJobs;
    return activeJobs.filter((j) => {
      const t = j.start ? new Date(j.start).getTime() : NaN;
      return !Number.isNaN(t) && t >= currentWeek.start && t <= currentWeek.end;
    });
  }, [activeJobs, userIsTech, currentWeek]);

  const groupedActiveJobs = useMemo(() => {
    const todayKey = todayStr();
    const validKey = (iso) => {
      const k = (iso || "").slice(0, 10);
      return /^\d{4}-\d{2}-\d{2}$/.test(k) ? k : "unscheduled";
    };
    const groups = {};
    for (const j of visibleActiveJobs) {
      const key = validKey(j.start);
      const label = key === "unscheduled" ? "Unscheduled" : dayLabelLocal(j.start);
      if (!groups[key]) groups[key] = { key, label, jobs: [] };
      groups[key].jobs.push(j);
    }
    const rank = (k) => (k === "unscheduled" ? 3 : k === todayKey ? 0 : k > todayKey ? 1 : 2);
    return Object.values(groups).sort((a, b) => {
      const ra = rank(a.key), rb = rank(b.key);
      return ra !== rb ? ra - rb : a.key.localeCompare(b.key);
    });
  }, [visibleActiveJobs]);

  const followUps = useMemo(() => {
    const pool = estimates.filter((e) => ["Draft", "Sent"].includes(e.status) && e.jobId);
    return (showTechView ? pool.filter((e) => myJobIds.has(e.jobId)) : pool)
      .sort((a, b) => (b.issuedAt || "").localeCompare(a.issuedAt || ""));
  }, [estimates, showTechView, myJobIds]);

  const myEstimates = useMemo(() => estimates.filter((e) => e.jobId && myJobIds.has(e.jobId)), [estimates, myJobIds]);
  const metrics = useMemo(() => computeMetrics(myJobs, myEstimates, tech?.performance, period), [myJobs, myEstimates, tech, period]);

  const tiles = useMemo(() => {
    const ratingVal = metrics.rating > 0 ? metrics.rating.toFixed(1) : "Not rated";
    const all = [
      { id: "jobs", label: "Jobs", value: String(metrics.jobs) },
      { id: "revenue", label: "Revenue", value: money0(metrics.revenue) },
      { id: "avg-ticket", label: "Avg Ticket", value: money0(metrics.avgTicket) },
      { id: "conversion", label: "Conversion Rate", value: `${Math.round(metrics.conversionRate)}%` },
      { id: "csat", label: "Customer Satisfaction", value: ratingVal, star: metrics.rating > 0 },
      { id: "reviews", label: "Reviews", value: String(metrics.reviews) },
    ];
    if (mode === "sales") return all.filter((t) => ["revenue", "avg-ticket", "conversion"].includes(t.id));
    return all;
  }, [metrics, mode]);

  /* ---------------- manager-perspective derived data ---------------- */
  const lowStockItems = useMemo(
    () => pricebook.filter((i) => isLowStock(i)).sort((a, b) => (a.stockOnHand || 0) - (b.stockOnHand || 0)).slice(0, 4),
    [pricebook],
  );
  const kpiCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Revenue Today", value: `$${(stats.revenueToday || 0).toLocaleString()}`, icon: DollarSign, bg: "#4CAF50" },
      { label: "Jobs Done", value: `${stats.jobsCompleted || 0}`, sub: `of ${stats.jobsScheduled || 0}`, icon: ClipboardCheck, bg: "#03A9F4" },
      { label: "Avg Ticket", value: `$${(stats.avgTicket || 0).toLocaleString()}`, icon: TrendingUp, bg: "#FF9800" },
      { label: "Active Techs", value: `${stats.activeTechs || 0}/${stats.technicianCount || 0}`, sub: `${stats.jobsUnassigned || 0} unassigned`, icon: Users, bg: "#9C27B0" },
    ];
  }, [stats]);
  // Tech's personal at-a-glance row — same 4-card visual language as the
  // manager's KPI row above (the full Scorecard with all 6 metrics + period
  // selector further down the page is unchanged and still has everything).
  const statCards = useMemo(() => {
    if (!showTechView) return kpiCards;
    return [
      { label: "Jobs", value: String(metrics.jobs), icon: Briefcase, bg: "#4CAF50" },
      { label: "Revenue", value: money0(metrics.revenue), icon: DollarSign, bg: "#03A9F4" },
      { label: "Avg Ticket", value: money0(metrics.avgTicket), icon: TrendingUp, bg: "#FF9800" },
      { label: "Rating", value: metrics.rating > 0 ? metrics.rating.toFixed(1) : "—", icon: Star, bg: "#9C27B0" },
    ];
  }, [showTechView, kpiCards, metrics]);
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? "Good Morning" : h < 18 ? "Good Afternoon" : "Good Evening";
  }, []);
  const todayLong = useMemo(() => fmtDateLong(new Date()), []);

  // "My Tasks" preview on Overview — same underlying `tasks` fetch used for
  // the Tasks-tab badge (mine-only for a tech, all/mine for a manager).
  const myPreviewTasks = useMemo(
    () => tasks.filter((t) => t.status !== "done").slice().sort((a, b) => (a.dueAt || "9999").localeCompare(b.dueAt || "9999")).slice(0, 4),
    [tasks],
  );
  const toggleTaskDone = async (t) => {
    try {
      await tasksApi.update(t.id, { status: t.status === "done" ? "open" : "done" });
      window.dispatchEvent(new Event("tasks-changed"));
    } catch {
      toast.error("Could not update task");
    }
  };

  /* ---------------- History tab date range (URL-synced) ---------------- */
  const [historyFrom, setHistoryFrom] = useState(() => searchParams.get("from") || getWeekBounds(new Date()).startStr);
  const [historyTo, setHistoryTo] = useState(() => searchParams.get("to") || getWeekBounds(new Date()).endStr);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeTab === "history" && historyFrom) next.set("from", historyFrom); else next.delete("from");
    if (activeTab === "history" && historyTo) next.set("to", historyTo); else next.delete("to");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyFrom, historyTo]);

  const openJob = (id) => navigate(`/mobile/jobs/${id}`);

  return (
    <div
      className="mv md:hidden min-h-screen w-full flex flex-col"
      style={{
        backgroundColor: "#F4F7FA",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-home"
    >
      {/* Preview bar (admin/dispatcher previewing a technician from /mobile) */}
      {isPreview && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50/60 border-b border-blue-100">
          <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Preview</span>
          <select
            value={tech?.id || ""}
            onChange={(e) => setPreviewTechId(e.target.value)}
            data-testid="mobile-preview-tech-select"
            className="flex-1 h-8 text-[12.5px] font-semibold bg-white border border-blue-200 rounded-lg px-2 text-slate-700"
          >
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button onClick={() => goBack("/dashboard")} data-testid="mobile-exit-preview-btn" className="shrink-0 h-8 px-2.5 rounded-lg text-[12px] font-semibold text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Exit
          </button>
        </div>
      )}

      <main className="flex-1 pb-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <div className="h-8 w-8 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin mb-3" />
            <div className="text-[13px]">Loading dashboard…</div>
          </div>
        ) : showTechView && !tech ? (
          <EmptyState icon={Wrench} title="No technician profile linked" subtitle="Your login isn't linked to a technician record yet. Ask an admin to set this up." />
        ) : (
          <>
            {activeTab === "overview" && (
              <div data-testid="mobile-overview">
                {/* Hero header — deep blue gradient spanning top bar through greeting */}
                <div
                  className="relative px-4 pt-3 pb-8 rounded-b-3xl shadow-md text-white"
                  style={{ background: `linear-gradient(to bottom, ${BRAND_BLUE}, ${BRAND_SKY})` }}
                  data-testid="mobile-home-hero"
                >
                  <div className="flex items-center justify-between">
                    {/* Invisible spacer — keeps the centered logo perfectly balanced
                        against the bell now that the hamburger is absolutely
                        positioned in the corner below, outside this flex row. */}
                    <div className="h-10 w-10 -ml-1" aria-hidden="true" />
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/mobile-header-logo`} alt="Service Guru Pro" className="h-[150px] w-[150px] object-contain" data-testid="mobile-home-logo" />
                    <button
                      onClick={() => setActiveTab("tasks")}
                      data-testid="mobile-home-bell-btn"
                      aria-label="Notifications"
                      className="relative h-10 w-10 rounded-full flex items-center justify-center active:bg-white/15 -mr-1"
                    >
                      <Bell className="h-6 w-6" color="#ffffff" />
                      {openTaskCount > 0 && (
                        <span data-testid="mobile-home-bell-badge" className="absolute top-1 right-1 min-w-[16px] h-[16px] px-[3px] rounded-full text-white text-[9.5px] font-bold flex items-center justify-center leading-none border-2" style={{ backgroundColor: "#EF4444", borderColor: BRAND_BLUE }}>
                          {openTaskCount > 9 ? "9+" : openTaskCount}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Hamburger — absolutely pinned to the header's top-left corner,
                      15% larger, independent of the centered logo row above. */}
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent("open-mobile-drawer"))}
                    data-testid="mobile-home-menu-btn"
                    aria-label="Open menu"
                    className="absolute h-11 w-11 rounded-full flex items-center justify-center active:bg-white/15"
                    style={{ top: "0.875rem", left: "0.875rem" }}
                  >
                    <Menu className="h-[27.6px] w-[27.6px]" color="#ffffff" />
                  </button>


                  <div className="flex items-center justify-between gap-3 mt-3">
                    <div className="min-w-0">
                      <div className="text-2xl font-bold flex items-center gap-2 flex-wrap leading-tight" data-testid="mobile-home-greeting">
                        {greeting}, {(user?.name || "there").split(" ")[0]}! <span aria-hidden="true">👋</span>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs text-white font-medium shrink-0" data-testid="mobile-home-date-pill">
                      <CalendarIcon className="h-3.5 w-3.5" /> {todayLong}
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-3 mt-1">
                    <div className="text-sm" style={{ color: "#E0F2FE" }}>Let's get the day started.</div>
                    <div
                      className="text-right text-lg font-semibold shrink-0"
                      style={{ color: "#E0F2FE", fontFamily: "'Dancing Script', cursive", transform: "rotate(-2deg)" }}
                      data-testid="mobile-home-slogan"
                    >
                      Work Smarter<br />Every Day
                    </div>
                  </div>
                </div>

                {/* Stats row */}
                {statCards.length > 0 && (
                  <div className="flex overflow-x-auto gap-3 -mt-6 px-4 no-scrollbar scroll-smooth relative z-10" data-testid="mobile-home-stats-row">
                    {statCards.map((k) => {
                      const Icon = k.icon;
                      return (
                        <div key={k.label} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-2 shrink-0 w-[130px]" data-testid={`stats-card-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
                          <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: k.bg }}>
                            <Icon className="h-5 w-5" color="#ffffff" strokeWidth={2.25} />
                          </div>
                          <div className="text-[19px] font-black leading-none" style={{ color: "#1E293B" }}>{k.value}</div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide leading-tight" style={{ color: "#64748B" }}>{k.label}</div>
                          {k.sub && <div className="text-[10.5px] text-slate-400 -mt-1">{k.sub}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* AI Assistant banner */}
                <div className="mx-4 mt-4 rounded-2xl shadow-sm relative overflow-hidden" data-testid="mobile-home-ai-banner">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/ai-banner?v=1e79d556`}
                    alt="Turn Conversations Into Revenue — AI Assistant"
                    className="w-full h-auto block"
                    data-testid="mobile-home-ai-banner-image"
                  />
                  <div className="absolute left-0 w-1/2 flex justify-center" style={{ top: "66%" }}>
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent("guru:voice-start"))}
                      data-testid="try-ai-assistant-button"
                      className="text-white px-4 py-2 text-xs font-semibold shadow-md active:scale-95 transition-transform inline-flex items-center gap-1.5 whitespace-nowrap"
                      style={{ backgroundColor: "#0063F7", borderRadius: "50px" }}
                    >
                      Try AI Assistant <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Today's Jobs */}
                <SectionCard title="Today's Jobs" rightAction={<TabLink onClick={() => navigate("/jobs")}>View All</TabLink>}>
                  {todaysJobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="mobile-home-jobs-empty">
                      <CalendarIcon className="h-9 w-9 text-slate-300 mb-2" strokeWidth={1.75} />
                      <div className="text-[13px] text-slate-400">No jobs scheduled today</div>
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100" data-testid="mobile-home-jobs">
                      {todaysJobs.map((j) => {
                        const color = STATUS_COLORS[j.status] || "#94A3B8";
                        return (
                          <li key={j.id}>
                            <button onClick={() => openJob(j.id)} data-testid={`mobile-home-job-${j.id}`} className="w-full text-left py-3 px-4 flex items-center gap-3 active:bg-slate-50">
                              <div className="h-11 w-11 rounded-xl flex flex-col items-center justify-center shrink-0" style={{ backgroundColor: `${color}18` }}>
                                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color }}>{fmtHour(j.start).split(" ")[1] || "AM"}</div>
                                <div className="text-[13px] font-black leading-none" style={{ color }}>{(fmtHour(j.start) || "—").split(" ")[0]}</div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-[15px] font-bold truncate" style={{ color: "#1E293B" }}>{j.customer || "—"}</div>
                                <div className="text-[12.5px] truncate" style={{ color: "#64748B" }}>{j.jobType || j.type || "Service"}</div>
                              </div>
                              <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: `${color}22`, color }}>{j.status || "Scheduled"}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="px-4 pb-4 pt-1">
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent("open-new-job"))}
                      data-testid="create-job-button"
                      className="w-full text-white py-3 rounded-xl font-semibold text-sm shadow-md flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      style={{ backgroundColor: BRAND_BLUE }}
                    >
                      <Plus className="h-4 w-4" /> Create Job
                    </button>
                  </div>
                </SectionCard>

                {/* My Tasks */}
                <SectionCard title="My Tasks" rightAction={<TabLink onClick={() => navigate("/tasks")}>View All</TabLink>}>
                  {myPreviewTasks.length === 0 ? <Empty label="No tasks assigned" /> : (
                    <ul className="divide-y divide-slate-100" data-testid="mobile-home-tasks-preview">
                      {myPreviewTasks.map((t) => {
                        const overdue = t.dueAt && new Date(t.dueAt) < new Date();
                        return (
                          <li key={t.id} className="py-3 px-4 flex items-center gap-3" data-testid={`mobile-home-task-${t.id}`}>
                            <button
                              onClick={() => toggleTaskDone(t)}
                              data-testid={`mobile-home-task-checkbox-${t.id}`}
                              aria-label="Toggle task complete"
                              className="shrink-0 active:scale-90 transition-transform"
                            >
                              {t.status === "done"
                                ? <CheckCircle2 className="h-5 w-5" style={{ color: BRAND_BLUE }} />
                                : <Circle className="h-5 w-5 text-slate-300" />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="text-[14px] font-bold truncate" style={{ color: "#1E293B" }}>{t.title}</div>
                              {t.dueAt && (
                                <div className="text-[11.5px] mt-0.5" style={{ color: overdue ? "#EF4444" : "#64748B" }}>
                                  {overdue ? "Overdue · " : "Due "}{fmtDueShort(t.dueAt)}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </SectionCard>

                {showTechView ? (
                  <Scorecard tech={tech} period={period} setPeriod={setPeriod} mode={mode} setMode={setMode} tiles={tiles} />
                ) : (
                  <>
                    {canSeeLowStock && lowStockItems.length > 0 && (
                      <SectionCard title="Low Stock" icon={<AlertTriangle className="h-4 w-4" color="#F59E0B" />} rightAction={<TabLink onClick={() => navigate("/pricebook")}>Manage</TabLink>}>
                        <ul className="divide-y divide-slate-100" data-testid="mobile-home-lowstock">
                          {lowStockItems.map((i) => (
                            <li key={i.id} className="py-3 px-4 flex items-center gap-3">
                              <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0"><Package className="h-5 w-5" color="#F59E0B" /></div>
                              <div className="min-w-0 flex-1">
                                <div className="text-[15px] font-semibold truncate" style={{ color: "#1E293B" }}>{i.name}</div>
                                <div className="text-[12px] truncate" style={{ color: "#64748B" }}>SKU {i.sku || "—"} · {i.stockOnHand || 0} on hand</div>
                              </div>
                              <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">Low</span>
                            </li>
                          ))}
                        </ul>
                      </SectionCard>
                    )}

                    <SectionCard title="Recent Activity" rightAction={hasModule("all_activity") ? <TabLink onClick={() => navigate("/mobile/activity")}>View All</TabLink> : null}>
                      {activity.length === 0 ? <Empty label="No recent activity yet" /> : (
                        <ul className="divide-y divide-slate-100" data-testid="mobile-home-activity">
                          {activity.slice(0, 8).map((a, idx) => {
                            const Icon = activityIconMap[a.type] || activityIconMap[a.icon] || activityIconMap[a.kind] || Briefcase;
                            const color = activityColorMap[a.type] || activityColorMap[a.icon] || activityColorMap[a.kind] || "#475569";
                            const rawLabel = (a.label || a.message || a.text || "").replace(/^[^\w]*/, "").trim();
                            const title = (rawLabel.split(" at ")[0] || rawLabel || "Activity").trim();
                            const subtitle = [a.customer, a.time].filter(Boolean).join(" · ");
                            return (
                              <li key={a.id || idx} className="py-3 px-4 flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}18` }}><Icon className="h-4 w-4" color={color} /></div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-[14px] font-bold truncate" style={{ color: "#1E293B" }}>{title}</div>
                                  <div className="text-[11.5px] mt-0.5 truncate" style={{ color: "#64748B" }}>{subtitle}</div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </SectionCard>
                  </>
                )}
              </div>
            )}

            {activeTab === "all-jobs" && (
              showTechView ? (
                <div data-testid="mobile-all-jobs" className="pt-2">
                  {visibleActiveJobs.length === 0 ? (
                    userIsTech ? <EmptyState icon={Inbox} title="No jobs scheduled for this week." /> : <EmptyState icon={Inbox} title="No active jobs" subtitle="Upcoming and in-progress work shows here." />
                  ) : (
                    groupedActiveJobs.map((g) => (
                      <div key={g.key} data-testid={`job-day-group-${g.key}`}>
                        <div className="px-4 pt-4 pb-1 flex items-center justify-between">
                          <SectionTitle>{g.label}</SectionTitle>
                          <span className="text-[11px] font-bold text-slate-400">{g.jobs.length} {g.jobs.length === 1 ? "job" : "jobs"}</span>
                        </div>
                        {g.jobs.map((j) => <MobileJobCard key={j.id} job={j} showDate onOpen={() => openJob(j.id)} />)}
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <MobileJobs />
              )
            )}

            {activeTab === "tasks" && <Tasks />}

            {activeTab === "follow-up" && (
              <div data-testid="mobile-follow-up" className="pt-2">
                {followUps.length === 0 ? (
                  <EmptyState icon={FileText} title="Nothing to follow up" subtitle="Open estimates needing action will appear here." />
                ) : (
                  followUps.map((e) => <EstimateCard key={e.id} est={e} />)
                )}
              </div>
            )}

            {activeTab === "history" && (
              <HistoryTab
                jobs={showTechView ? myJobs : jobs}
                from={historyFrom}
                to={historyTo}
                onFromChange={setHistoryFrom}
                onToChange={setHistoryTo}
                onOpenJob={openJob}
              />
            )}

            {activeTab === "guru" && (
              <div data-testid="mobile-guru">
                <GuruLauncher onOpen={() => navigate("/mobile/guru")} />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Local re-implementation of dayLabel to avoid an extra shared-file hop for a one-liner.
function dayLabelLocal(iso) {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const that = new Date(d);
  that.setHours(0, 0, 0, 0);
  const diff = Math.round((that - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
}

/* ---------------- sub-components ---------------- */
function SectionTitle({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{children}</div>;
}

function SectionCard({ title, icon, rightAction, children }) {
  return (
    <div className="mx-4 mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
        <div className="inline-flex items-center gap-1.5">{icon}<div className="text-[15px] font-bold" style={{ color: "#0B1E3F" }}>{title}</div></div>
        {rightAction}
      </div>
      {children}
    </div>
  );
}

function TabLink({ onClick, children }) {
  return (
    <button onClick={onClick} className="text-[13px] font-bold text-blue-600 active:opacity-70 inline-flex items-center gap-0.5">
      {children} <ChevronRight className="h-4 w-4" />
    </button>
  );
}

function Empty({ label }) {
  return <div className="py-6 px-4 text-center text-[13px] text-slate-400">{label}</div>;
}

function Scorecard({ tech, period, setPeriod, mode, setMode, tiles }) {
  const initials = (name = "") => name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
  return (
    <div className="bg-white rounded-2xl p-5 mt-4 mx-4 border border-slate-200" data-testid="mobile-scorecard">
      <div className="text-xl font-bold text-[#0B1E3F] mb-4">Scorecard</div>
      <div className="flex items-center gap-3 mb-5">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-sm" style={{ background: tech.color || "#3B5170" }}>
            {tech.avatar || initials(tech.name)}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-blue-600 border-2 border-white" />
        </div>
        <div className="min-w-0">
          <div className="text-[18px] font-bold text-[#0B1E3F] truncate leading-tight">{tech.name}</div>
          <div className="text-[13px] text-slate-400 truncate">{tech.role || "Technician"}</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="relative">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="scorecard-period-dropdown" className="h-10 pl-4 pr-9 text-[13px] font-semibold bg-slate-100 rounded-full text-slate-700 appearance-none">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <ChevronDown className="h-4 w-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="inline-flex items-center bg-slate-100 rounded-full p-1 shrink-0">
          {[{ key: "total", label: "Total", testid: "scorecard-toggle-total" }, { key: "sales", label: "Sales", testid: "scorecard-toggle-sales" }].map((m) => (
            <button key={m.key} onClick={() => setMode(m.key)} data-testid={m.testid} className={`px-5 py-1.5 rounded-full text-[13px] font-bold transition-all ${mode === m.key ? "bg-blue-600 text-white" : "text-slate-400"}`}>{m.label}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3" data-testid="scorecard-kpi-grid">
        {tiles.map((t) => (
          <div key={t.id} data-testid={`kpi-tile-${t.id}`} className="bg-slate-50 rounded-xl p-3.5">
            <div className="text-[12px] text-slate-400 leading-tight min-h-[28px]">{t.label}</div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[22px] font-black tracking-tight text-[#0B1E3F] leading-none">{t.value}</span>
              {t.star && <Star className="h-4 w-4 text-amber-400 fill-amber-400" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EstimateCard({ est }) {
  return (
    <div data-testid={`follow-up-${est.id}`} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-3 mx-4 max-w-[calc(100%-2rem)]">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-[#0B1E3F] truncate">{est.customer}</div>
          <div className="text-[11px] text-slate-500 truncate">{est.title || est.id}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${est.status === "Sent" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{est.status}</span>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <span className="text-[11px] text-slate-400 font-mono">{est.id}</span>
        <span className="text-[15px] font-black text-[#0B1E3F]">{money0(est.total)}</span>
      </div>
    </div>
  );
}

function GuruLauncher({ onOpen }) {
  return (
    <div className="mx-4 mt-4 bg-gradient-to-br from-[#0B1E3F] to-[#2563EB] rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
      <div className="absolute -right-6 -top-6 opacity-10"><Bot className="h-32 w-32" strokeWidth={1.5} /></div>
      <div className="relative">
        <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white/15 mb-3"><Bot className="h-6 w-6 text-white" strokeWidth={2.5} /></div>
        <div className="text-xl font-black mb-1.5">Ask Diagnostic Guru</div>
        <div className="text-blue-100 text-sm mb-5 leading-relaxed">Your AI diagnostic assistant — snap a photo of a unit, ask a question, or pull up a troubleshooting flow.</div>
        <button onClick={onOpen} data-testid="mobile-guru-open-btn" className="bg-white text-[#2563EB] font-bold py-3 px-6 rounded-xl w-full text-sm shadow-sm active:bg-blue-50 active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2">
          Open Guru Assistant <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle, compact }) {
  return (
    <div className={`flex flex-col items-center justify-center px-4 text-center ${compact ? "py-8" : "py-14"}`}>
      <Icon className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.75} />
      <div className="text-base font-bold text-slate-700">{title}</div>
      {subtitle && <div className="text-sm text-slate-500 mt-1 max-w-[260px]">{subtitle}</div>}
    </div>
  );
}
